/*
  Maximum shipping weight notice and block.

  This is a warning to the customer, NOT a rule. A browser can walk around it:
  /checkout is reachable by URL and /cart/add.js by hand. To actually stop the
  order you need a shipping rate with a weight condition, or a Cart Validation
  Function.

  Three things it has to survive, learned the hard way:

    - the cart weight rendered by Liquid goes stale the moment something is
      added through the side drawer, which does not reload;
    - Dawn re-enables the add button in toggleAddButton (global.js), which
      calls removeAttribute('disabled') on every variant change;
    - the force guard (cart-force-select.js) assigns `disabled` outright on
      every cart update, so it has to be told about this block or it lifts it.
*/

(() => {
  const instances = new Set();

  // Cart weight, shared: one read serves both the product page and the cart.
  const cart = { weight: 0, items: [], unitGrams: () => 0 };

  /*
    /cart.js does not promise whether items[].grams is the weight of one unit
    or of the whole line. Rather than guess, it is checked against
    total_weight, which is reliable, and the reading that adds up is used.
  */
  function readUnitGrams(payload) {
    const items = payload.items || [];
    const asUnit = items.reduce((total, item) => total + item.grams * item.quantity, 0);
    if (asUnit === payload.total_weight) return (item) => item.grams;
    return (item) => (item.quantity ? item.grams / item.quantity : 0);
  }

  async function refreshCart() {
    const base = (window.routes && window.routes.cart_url) || '/cart';
    try {
      const response = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const payload = await response.json();
      cart.weight = payload.total_weight || 0;
      cart.items = payload.items || [];
      cart.unitGrams = readUnitGrams(payload);
    } catch (error) {
      // With no network the last known weight is kept: a stale notice beats a
      // form locked for no visible reason.
      return;
    }
    instances.forEach((instance) => instance.apply());
  }

  function toInt(value, fallback = 0) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  class ThWeightLimit extends HTMLElement {
    connectedCallback() {
      this.limit = toInt(this.dataset.limit);
      this.context = this.dataset.context || 'product';
      this.notices = {
        exceeded: this.querySelector('[data-th-weight-notice="exceeded"]'),
        reached: this.querySelector('[data-th-weight-notice="reached"]'),
      };

      // First value from the server, so nothing flickers before /cart.js.
      if (!cart.weight) cart.weight = toInt(this.dataset.cartWeight);

      this.unitWeights = {};
      if (this.dataset.unitWeights) {
        try {
          this.unitWeights = JSON.parse(this.dataset.unitWeights);
        } catch (error) {
          this.unitWeights = {};
        }
      }

      instances.add(this);
      this.listen();
      this.apply();
      refreshCart();
    }

    disconnectedCallback() {
      instances.delete(this);
      this.unsubscribers.forEach((off) => off());
      this.unsubscribers = [];
    }

    listen() {
      this.unsubscribers = [];

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        // cartUpdate: the weight changed. variantChange: Dawn has just
        // re-enabled the button in toggleAddButton and it must be blocked again.
        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => refreshCart()),
          subscribe(PUB_SUB_EVENTS.variantChange, () => this.apply())
        );
      }

      this.onFormInput = (event) => {
        if (event.target.matches('input[name="quantity"], [name="id"], input[name="updates[]"]')) {
          this.apply();
        }
      };
      document.addEventListener('change', this.onFormInput);
      document.addEventListener('input', this.onFormInput);
      this.unsubscribers.push(() => {
        document.removeEventListener('change', this.onFormInput);
        document.removeEventListener('input', this.onFormInput);
      });
    }

    get form() {
      const id = this.dataset.formId;
      return (id && document.getElementById(id)) || document.querySelector('form[action*="/cart/add"]');
    }

    // What the cart would weigh if what is on screen were accepted.
    projectedWeight() {
      if (this.context !== 'product') return cart.weight;

      const form = this.form;
      if (!form) return cart.weight;

      const variantField = form.querySelector('[name="id"]');
      // In Dawn the quantity selector is a block of its own and sits outside
      // the form, so it has to be looked up in the document too. The cart ones
      // are called updates[], so they do not collide.
      const quantityField =
        form.querySelector('input[name="quantity"]') ||
        document.querySelector('input[name="quantity"]');

      const unit = this.unitWeights[variantField && variantField.value] || 0;
      const quantity = Math.max(1, toInt(quantityField && quantityField.value, 1));
      return cart.weight + unit * quantity;
    }

    apply() {
      if (!this.limit) return;

      if (this.context === 'product') {
        const exceeded = this.projectedWeight() > this.limit;
        this.toggleNotice(exceeded ? 'exceeded' : null);
        this.toggleProduct(exceeded);
        return;
      }

      /*
        In the cart the "+" already stops anyone going over, so 'exceeded' is
        only seen when the cart arrived heavy: added in one go from the product
        page, or the limit lowered afterwards. The normal case is landing
        exactly on the limit, and that is what 'reached' explains.
      */
      const exceeded = cart.weight > this.limit;
      const hasRoom = this.capCartQuantities();
      this.toggleNotice(exceeded ? 'exceeded' : hasRoom ? null : 'reached');
      this.toggleCart(exceeded);
    }

    toggleNotice(state) {
      this.hidden = !state;
      Object.keys(this.notices).forEach((key) => {
        const node = this.notices[key];
        if (node) node.hidden = key !== state;
      });
    }

    toggleProduct(exceeded) {
      const form = this.form;
      if (!form) return;

      const addButton = form.querySelector('[name="add"]');
      if (addButton) this.setBlocked(addButton, exceeded);

      // Accelerated checkout skips the cart, so it is taken away too.
      const accelerated = form.parentElement
        ? form.parentElement.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout')
        : [];
      accelerated.forEach((node) => {
        node.toggleAttribute('hidden', exceeded);
      });
    }

    toggleCart(exceeded) {
      document
        .querySelectorAll('#checkout, #CartDrawer-Checkout, [name="checkout"]')
        .forEach((button) => this.setBlocked(button, exceeded));

      document
        .querySelectorAll('.additional-checkout-buttons')
        .forEach((node) => node.toggleAttribute('hidden', exceeded));
    }

    /*
      Marked with data-th-weight-blocked so a button disabled for another
      reason (sold out, quantity rules, a spring with no force chosen) is never
      re-enabled here. cart-force-select.js reads the same flag for the
      opposite case: so that its own sync does not lift this block.
    */
    setBlocked(button, blocked) {
      if (blocked) {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.thWeightBlocked = 'true';
        }
      } else if (button.dataset.thWeightBlocked === 'true') {
        button.disabled = false;
        delete button.dataset.thWeightBlocked;
      }
    }

    /*
      In the cart the "+" leans on stepUp(), which respects the max attribute,
      and QuantityInput uses it to grey the button out. Lowering each line's
      max to whatever fits before going over the weight is enough.

      Returns whether any line still has room. With every line at the top,
      whoever is looking at the screen sees all the "+" greyed out and deserves
      an explanation.
    */
    capCartQuantities() {
      let lines = 0;
      let withRoom = 0;

      document.querySelectorAll('input[name="updates[]"][data-index]').forEach((input) => {
        const item = cart.items[toInt(input.dataset.index) - 1];
        if (!item) return;
        lines += 1;

        const unit = cart.unitGrams(item);
        /*
          A weight of 0 -or none declared- uses no allowance, so that line
          always has room. Without this the line counted towards 'lines' and
          never towards 'withRoom': a cart holding only a weightless item
          claimed the limit had been reached. The gas spring refills are sold
          as a service with no shipping, so they hit this every time.
        */
        if (unit <= 0) {
          withRoom += 1;
          return;
        }

        const others = cart.weight - unit * item.quantity;
        const room = Math.max(1, Math.floor((this.limit - others) / unit));

        const ruleMax = toInt(input.dataset.thWeightRuleMax || input.max, 0);
        if (input.dataset.thWeightRuleMax === undefined && input.max) {
          input.dataset.thWeightRuleMax = input.max;
        }

        const max = ruleMax > 0 ? Math.min(ruleMax, room) : room;
        if (toInt(input.max, -1) !== max) input.max = String(max);

        // Typing a bigger number by hand does not go through stepUp, so it has
        // to be clamped here.
        if (toInt(input.value, 1) > max) {
          input.value = String(max);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (toInt(input.value, 1) < max) withRoom += 1;

        const wrapper = input.closest('quantity-input');
        if (wrapper && typeof wrapper.validateQtyRules === 'function') wrapper.validateQtyRules();
      });

      // With no lines measured nothing is claimed: the cart may be empty, or
      // /cart.js may not have answered yet.
      return lines === 0 || withRoom > 0;
    }
  }

  if (!customElements.get('th-weight-limit')) {
    customElements.define('th-weight-limit', ThWeightLimit);
  }
})();
